import {
    CRM_INTEGRATION_PROVIDERS,
    type CrmIntegrationProvider,
} from "app/pages/sales-intelligence/domain/crm-integration/types/provider";
import type salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";

import { excludeByCrmCapability, type LeadsCapability, leadsCapabilityMap } from "../types/leadsCapability";

export type CreateLeadsGrantsInput = Partial<{
    readonly isExcludeSectionAvailable: boolean;
    readonly isCrmFilterAvailable: boolean;
    readonly isCrmScopeFilterAvailable: boolean;
    readonly isCompanyFilterAvailable: boolean;
    readonly activeCrmIntegration: CrmIntegrationProvider;
}>;

/**
 * Resolves a surface's page intent + workspace entitlements into the leads capabilities that gate filters,
 * so the surface stays filter-agnostic (ADR-0008/0012). The grant set drives filter visibility by set
 * containment; per-surface non-capability defaults (e.g. the suppression-list seed) are passed to the
 * leads config directly, never through this set.
 */
export const createLeadsGrants = (
    settings: typeof salesSettingsHelper,
    {
        isExcludeSectionAvailable = false,
        isCrmFilterAvailable = false,
        isCrmScopeFilterAvailable = false,
        isCompanyFilterAvailable = false,
        activeCrmIntegration,
    }: CreateLeadsGrantsInput,
): ReadonlySet<LeadsCapability> => {
    const grants = new Set<LeadsCapability>();

    if (isExcludeSectionAvailable) {
        grants.add(leadsCapabilityMap.excludeFilters);
    }
    if (isCompanyFilterAvailable) {
        grants.add(leadsCapabilityMap.companyFilter);
    }
    if (isCrmFilterAvailable) {
        grants.add(leadsCapabilityMap.crmLegacyFilter);
    }
    if (isCrmScopeFilterAvailable) {
        grants.add(leadsCapabilityMap.crmScope);
    }
    if (settings.isCrmCustomFiltersEnabled) {
        grants.add(leadsCapabilityMap.crmCustomFilters);
    }

    // Per-provider exclude-by-CRM: the old `isExcludeByCrmFilterHidden` predicate, resolved once into grants.
    const isExcludeByCrmAvailable = settings.isCrmIntegrationsEnabled();
    for (const provider of CRM_INTEGRATION_PROVIDERS) {
        const hidden =
            !isExcludeByCrmAvailable ||
            !isExcludeSectionAvailable ||
            // The active integration's CRM custom filters supersede its exclude-by-CRM filter.
            (settings.isCrmCustomFiltersEnabled && provider === activeCrmIntegration);

        if (!hidden) {
            grants.add(excludeByCrmCapability(provider));
        }
    }

    return grants;
};
